import { useEffect, useRef, useState } from 'react';
import { procurementService } from '../services/api';
import { formatNumeroCompraComAno } from '../utils/formatters';

/**
 * Vigia de CONVOCAÇÕES — alerta FORTE dentro do HUB quando chega uma mensagem
 * que cita a IPCOM (nome/CNPJ) ou é ato de convocação (convoca/negocia/habilitação/
 * diligência/lance fechado). Espelha o alerta do Telegram, pra quem está com o Hub
 * aberto não depender do celular.
 *
 * - Poll a cada 60s em /procurement/convocacoes/recent (48h).
 * - 1ª carga só "semeia" o que já existe (não alerta backlog).
 * - Nova convocação → beep + Notification do navegador + título piscando + banner.
 * - IDs vistos persistem em localStorage (não re-alerta no reload).
 */
interface Convocacao {
  id: number;
  itemId: number;
  mentionsIpcom: boolean;
  isConvocacao: boolean;
  origin: string;
  messageText: string;
  orgaoNome: string | null;
  numeroCompra: string | null;
  anoCompra: number | string | null;
  codigoUnidade: string | null;
  createdAt: string | null;
}

const SEEN_KEY = 'convocacaoSeenIds';

function playBeep() {
  try {
    const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.type = 'sine';
    g.gain.value = 0.25;
    const t = ctx.currentTime;
    // três bipes (urgente)
    o.frequency.setValueAtTime(880, t);
    o.frequency.setValueAtTime(660, t + 0.18);
    o.frequency.setValueAtTime(880, t + 0.36);
    o.start(t);
    o.stop(t + 0.6);
    o.onended = () => ctx.close().catch(() => {});
  } catch {
    /* autoplay bloqueado sem gesto — banner+notif ainda disparam */
  }
}

function blinkTitle() {
  const orig = document.title;
  let on = false;
  let n = 0;
  const iv = setInterval(() => {
    on = !on;
    document.title = on ? '🔴 CONVOCAÇÃO — IPCOM' : orig;
    if (++n > 24) {
      clearInterval(iv);
      document.title = orig;
    }
  }, 700);
}

export function ConvocacaoWatcher({ onOpen }: { onOpen?: (itemId: number) => void }) {
  const seenRef = useRef<Set<number>>(new Set());
  const initedRef = useRef(false);
  const [banner, setBanner] = useState<{ c: Convocacao; count: number } | null>(null);

  useEffect(() => {
    try {
      seenRef.current = new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]'));
    } catch {
      /* ignore */
    }
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }

    let stopped = false;
    const persist = () => {
      try {
        localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(seenRef.current).slice(-500)));
      } catch {
        /* ignore */
      }
    };

    const alertNew = (c: Convocacao, count: number) => {
      setBanner({ c, count });
      playBeep();
      blinkTitle();
      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          const n = new Notification(`🔴 CONVOCAÇÃO${c.mentionsIpcom ? ' — IPCOM CHAMADA' : ''}`, {
            body: `${formatNumeroCompraComAno(c.numeroCompra || '?', c.anoCompra)}${c.codigoUnidade ? ` · UASG ${c.codigoUnidade}` : ''} — ${c.orgaoNome || ''}\n${(c.messageText || '').slice(0, 140)}`,
            requireInteraction: true,
            tag: `convoca-${c.id}`,
          });
          n.onclick = () => {
            window.focus();
            n.close();
          };
        } catch {
          /* ignore */
        }
      }
    };

    const RECENT_MS = 10 * 60 * 1000; // só alerta convocação dos últimos 10min
    const poll = async () => {
      try {
        const list: Convocacao[] = (await procurementService.getRecentConvocacoes()) || [];
        const now = Date.now();
        const fresh = list.filter((c) => !seenRef.current.has(c.id));
        if (!fresh.length) return;
        // Marca todas as não-vistas como vistas (não re-alerta as antigas/backlog).
        fresh.forEach((c) => seenRef.current.add(c.id));
        persist();
        // Alerta SÓ as recentes (≤10min) — cobre "abri o Hub logo após a convocação"
        // E as novas que chegam durante a sessão; backlog antigo só semeia.
        const recentes = fresh.filter((c) => {
          const t = new Date(c.createdAt || '').getTime();
          return !isNaN(t) && now - t < RECENT_MS;
        });
        if (recentes.length) alertNew(recentes[0], recentes.length); // [0] = mais recente (lista DESC)
        initedRef.current = true;
      } catch {
        /* rede/401 — silencioso */
      }
    };

    poll();
    const iv = setInterval(() => {
      if (!stopped) poll();
    }, 60000);
    return () => {
      stopped = true;
      clearInterval(iv);
    };
  }, []);

  if (!banner) return null;
  const { c, count } = banner;
  return (
    <div className="fixed top-4 right-4 z-[9999] w-[380px] max-w-[92vw] rounded-xl border border-rose-500 bg-rose-950/95 shadow-2xl shadow-rose-900/50 backdrop-blur animate-pulse-slow">
      <div className="flex items-start gap-3 p-4">
        <div className="text-2xl leading-none">🔴</div>
        <div className="flex-1 min-w-0">
          <div className="text-rose-100 font-bold text-sm">
            CONVOCAÇÃO{c.mentionsIpcom ? ' — IPCOM CHAMADA' : ''}
            {count > 1 && <span className="ml-1 text-rose-300 font-normal">(+{count - 1})</span>}
          </div>
          <div className="text-rose-200/90 text-xs mt-0.5">
            {formatNumeroCompraComAno(c.numeroCompra || '?', c.anoCompra)}
            {c.codigoUnidade ? ` · UASG ${c.codigoUnidade}` : ''} — {(c.orgaoNome || '').slice(0, 50)}
          </div>
          <div className="text-rose-100/80 text-xs mt-1.5 line-clamp-3">
            {(c.messageText || '').slice(0, 220)}
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => {
                setBanner(null);
                onOpen?.(c.itemId);
              }}
              className="px-3 py-1.5 rounded bg-rose-600 hover:bg-rose-500 text-white text-xs font-medium"
            >
              Ver no Hub
            </button>
            <button
              onClick={() => setBanner(null)}
              className="px-3 py-1.5 rounded bg-rose-900 hover:bg-rose-800 text-rose-200 text-xs"
            >
              Dispensar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ConvocacaoWatcher;
